import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import "../styles/TeamsMemberList.css";
import { fetchGroupMap } from "components/API/ApiEnpoints";
import { highlightText } from "components/utils/SearchHighlight";
import { HiUsers } from "react-icons/hi";
import { HiOutlineBars3, HiOutlineSquares2X2, HiXMark } from "react-icons/hi2";
import { FaUserCheck, FaUserTimes, FaUserCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaIdBadge } from "react-icons/fa";
import { HiOutlineCalendar, HiOutlineClock } from "react-icons/hi2";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
import { SessionExtend } from "components/utils/SessionCheckUtils";
import {
  FaEnvelope,
  FaBuilding,
  FaCity,
  FaUsers,
  FaPhoneAlt,
  FaGlobe,
} from "react-icons/fa";
import {
  MdOutlineDeviceUnknown,
  MdOutlineNotificationsActive,
} from "react-icons/md";

interface User {
  user_uid: string;
  user_name: string;
  job_title?: string;
  date_added: string;
  user_status: string;
  last_active: string;
  icon_image: string;
}

interface Group {
  org_uid: string;
  group_uid: string;
  group_name: string;
  users_list: User[];
}

const TeamsMemberList: React.FC = () => {
  useCheckSessionHeartBeat();
  const { group_uid } = useParams<{ group_uid: string }>();
  const location = useLocation();
  const groupUid = location.state?.groupUid;
  const orgUid = location.state?.orgUid;
  const userCount = location.state?.userCount;
  const activeUsers = location.state?.activeUsers;

  const sessionUid = localStorage.getItem("session_uid");
  const userUid = localStorage.getItem("user_uid");

  const [groupData, setGroupData] = useState<Group[]>([]);
  const [viewMode, setViewMode] = useState<"row" | "card">("row");
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [showUnregisteredOnly, setShowUnregisteredOnly] = useState(false);

  const [selectedUserProfile, setSelectedUserProfile] = useState<any>(null);
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);

  const navigate = useNavigate();

  const params = { group_uid: groupUid, org_uid: orgUid };

  const getGroupMapData = async () => {
    if (!sessionUid || !userUid) {
      console.error("Session or User UID missing");
      return;
    }
    setLoadingMembers(true);

    try {
      const apiResponse = await fetchGroupMap(sessionUid, userUid, params);

      if (apiResponse.status === "Success") {
        setGroupData(apiResponse.data);
      } else {
        console.error("Fetch group map failed:", apiResponse.message);
        setGroupData([]);
      }
      SessionExtend();
    } catch (err) {
      console.error("Unexpected error while fetching group map:", err);
      setGroupData([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchUserProfile = async (user_uid: string, iconImage?: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/public-user-profile`,
        {
          headers: {
            "user-uid": userUid!,
            "Content-Type": "application/json",
          }, withCredentials: true,
          params: {
            target_user_uid: user_uid,
          },
        }
      );

      if (
        response.data.status === "Success" &&
        typeof response.data.data === "object"
      ) {
        const userData = response.data.data;

        // Inject base64 image only if it's provided and valid
        if (iconImage && iconImage.trim() !== "") {
          let mimeType = "image/png";
          if (iconImage.startsWith("R0lGODlh")) mimeType = "image/gif";
          else if (iconImage.startsWith("/9j/")) mimeType = "image/jpeg";
          else if (iconImage.startsWith("iVBORw0KGgo")) mimeType = "image/png";

          userData.image = `data:${mimeType};base64,${iconImage}`;
        } else {
          userData.image = null; // fallback handled in UI
        }

        setSelectedUserProfile(userData);
        setIsProfilePopupOpen(true);
      } else {
        console.error("Invalid profile response");
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  useEffect(() => {
    getGroupMapData();
  }, []);

  const handleViewToggle = (mode: "row" | "card") => {
    setViewMode(mode);
  };
  const [totalMembers, registeredCount, unregisteredCount] = useMemo(() => {
    const users = groupData[0]?.users_list || [];
    const registered = users.filter((u) => u.user_status === "active").length;
    const unregistered = users.filter(
      (u) => u.user_status === "inactive"
    ).length;
    return [users.length, registered, unregistered];
  }, [groupData]);

  const getStatusInfo = (status: string) => {
    switch (status) {
      case "active":
        return { color: "#16821aff", label: "Online", iconname: "checkcircle" }; // green 16821aff
      case "away":
        return { color: "#f6c70aff", label: "Away", iconname: "clockcircle" }; // yellow f6c70aff
      case "invited":
        return {
          color: "#000000",
          label: "Inactive",
          iconname: "exclamationcircle",
        }; // black
      default:
        return {
          color: "#9b9292ff",
          label: "Offline",
          iconname: "closecircle",
        }; // black
    }
  };
  const filteredUsers = useMemo(() => {
    const users = groupData[0]?.users_list || [];
    return users
      .filter((user) =>
        user.user_name.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .filter((user) => {
        if (showActiveOnly) return user.user_status === "active";
        if (showUnregisteredOnly) return user.user_status === "inactive";
        return true;
      })
      .sort((a, b) => {
        if (a.user_status === "active" && b.user_status !== "active") return -1;
        if (a.user_status !== "active" && b.user_status === "active") return 1;
        return 0;
      });
  }, [groupData, searchQuery, showActiveOnly, showUnregisteredOnly]);

  return (
    <div className="ManageTeamList-full-container">
      <div className="ManageTeamList-summary-enhanced">
        <div className="ManageTeamList-title">
          <HiUsers className="team-icon" />
          <h2>{groupData[0]?.group_name || "Team Name"}</h2>
        </div>
        <div className="ManageTeamList-stats">
          <div className="ManageTeamList-stat-card total">
            <span className="icon">
              <HiUsers style={{ color: "white" }} />
            </span>
            <span className="label">Total Members</span>
            <span className="value">{totalMembers}</span>
          </div>
          <div className="ManageTeamList-stat-card active">
            <span className="icon">
              <FaUserCheck style={{ color: "white" }} />
            </span>
            <span className="label">Registered Members</span>
            <span className="value">{registeredCount}</span>
          </div>
          <div className="ManageTeamList-stat-card inactive">
            <span className="icon">
              <FaUserTimes style={{ color: "white" }} />
            </span>
            <span className="label">Yet to register</span>
            <span className="value">{unregisteredCount}</span>
          </div>
        </div>
      </div>

      <div className="ManageTeamList-members-header">
        <div className="ManageTeamList-top-controls">
          <div className="ManageTeamList-filter-bar">
            <label className="filter-toggle">
              <input
                type="checkbox"
                checked={showActiveOnly}
                onChange={(e) => {
                  setShowActiveOnly(e.target.checked);
                  if (e.target.checked) setShowUnregisteredOnly(false);
                }}
              />
              <span>Show Registered Users Only</span>
            </label>
            <label className="filter-toggle">
              <input
                type="checkbox"
                checked={showUnregisteredOnly}
                onChange={(e) => {
                  setShowUnregisteredOnly(e.target.checked);
                  if (e.target.checked) setShowActiveOnly(false); // mutually exclusive
                }}
              />
              <span>Show Unregistered Users Only</span>
            </label>
          </div>

          <div className="ManageTeamList-view-toggle">
            <button
              className={`ManageTeamList-view-btn ${viewMode === "row" ? "active" : ""
                }`}
              onClick={() => handleViewToggle("row")}
            >
              <HiOutlineBars3 size={18} /> Row
            </button>
            <button
              className={`ManageTeamList-view-btn ${viewMode === "card" ? "active" : ""
                }`}
              onClick={() => handleViewToggle("card")}
            >
              <HiOutlineSquares2X2 size={18} /> Card
            </button>
          </div>
          <button
            className="ManageTeamList-view-leaderboard-btn"
            onClick={() =>
              navigate("/public-leaderboard", {
                state: {
                  group_uid: groupUid,
                  group_name: groupData[0]?.group_name || "Team",
                },
              })
            }
          >
            View Team Leaderboard
          </button>
          <div className="ManageTeamList-search-container">
            <input
              type="text"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="ManageTeamList-search-input"
            />
          </div>
        </div>
      </div>
      {loadingMembers ? (
        <div className="ManageTeamList-loader">Loading team members...</div>
      ) : (
        <>
          {filteredUsers.length > 0 ? (
            <div
              className={`ManageTeamList-members-rows ${viewMode === "card" ? "card-view" : "row-view"
                }`}
            >
              {filteredUsers.map((user, index) => (
                <div
                  key={index}
                  className={`${viewMode === "row"
                    ? "ManageTeamList-user-row"
                    : "ManageTeamList-user-card"
                    } ${user.user_status === "inactive" ? "inactive-user" : ""}`}
                  onClick={() => fetchUserProfile(user.user_uid, user.icon_image)}
                  title={
                    user.user_status !== "active" ? "User not Registered" : ""
                  }
                >
                  {user.icon_image?.trim() ? (
                    <img
                      className={
                        viewMode === "row"
                          ? "ManageTeamList-user-icon"
                          : "ManageTeamList-user-icon large"
                      }
                      src={`data:image/jpeg;base64,${user.icon_image}`}
                      alt="User Icon"
                    />
                  ) : (
                    <FaUserCircle
                      className={
                        viewMode === "row"
                          ? "ManageTeamList-fallback-icon"
                          : "ManageTeamList-fallback-icon large"
                      }
                    />
                  )}
                  {viewMode === "row" ? (
                    <>
                      <div className="ManageTeamList-user-info">
                        <h4>{highlightText(user.user_name, searchQuery)}</h4>
                        <p>{highlightText(user.job_title || "—", searchQuery)}</p>
                      </div>
                      <div className="ManageTeamList-user-last-active">
                        {user.user_status === "active" &&
                          user.last_active?.trim() ? (
                          <>
                            <span className="last-active-label">
                              Last active:
                            </span>{" "}
                            {new Date(user.last_active).toLocaleString(
                              undefined,
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              }
                            )}
                          </>
                        ) : user.user_status === "inactive" ? (
                          "Unregistered"
                        ) : (
                          ""
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <h4>{highlightText(user.user_name, searchQuery)}</h4>
                      <p className="title">
                        {highlightText(user.job_title || "—", searchQuery)}
                      </p>
                      <p className="last-active">
                        {user.user_status === "active" &&
                          user.last_active?.trim() ? (
                          <>
                            <span className="last-active-label">
                              Last active:
                            </span>
                            {new Date(user.last_active).toLocaleString(
                              undefined,
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              }
                            )}
                          </>
                        ) : user.user_status === "inactive" ? (
                          "Unregistered"
                        ) : (
                          ""
                        )}
                      </p>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            /* Handle Empty State */
            <div className="ManageTeamList-no-data">
              {searchQuery
                ? `No users found matching "${searchQuery}"`
                : "No users found matching the selected filter."}
            </div>
          )}
        </>
      )}
      {isProfilePopupOpen && selectedUserProfile && (
        <div className="member-profile-popup">
          <div className="member-profile-popup-content">
            <span
              className="member-profile-close"
              onClick={() => setIsProfilePopupOpen(false)}
            >
              &times;
            </span>

            <div className="member-profile-header">
              {selectedUserProfile.image ? (
                <img
                  src={selectedUserProfile.image}
                  alt={`${selectedUserProfile.first_name} ${selectedUserProfile.last_name}`}
                  className="member-profile-image"
                  style={{
                    border: `3px solid ${getStatusInfo(selectedUserProfile.user_online_status)
                      .color
                      }`,
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <FaUserCircle
                  className="member-profile-placeholder"
                  style={{
                    color: getStatusInfo(selectedUserProfile.user_online_status)
                      .color,
                    border: `3px solid ${getStatusInfo(selectedUserProfile.user_online_status)
                      .color
                      }`,
                    borderRadius: "50%",
                    padding: "3px",
                  }}
                />
              )}
              <div
                style={{
                  textAlign: "center",
                  marginTop: "2px",
                  fontWeight: "500",
                  color: getStatusInfo(selectedUserProfile.user_online_status)
                    .color,
                }}
              >
                {getStatusInfo(selectedUserProfile.user_online_status).label}
              </div>
              <div className="member-name-job">
                <h2>{`${selectedUserProfile.first_name} ${selectedUserProfile.last_name}`}</h2>
                <p className="member-job-title">
                  {selectedUserProfile.job_title}
                </p>
                <p
                  className={`status-label ${selectedUserProfile.user_status === "active"
                    ? "active"
                    : "inactive"
                    }`}
                >
                  {selectedUserProfile.user_status === "active" ? (
                    <span className="status-tag MemeberListregistered">
                      Registered
                    </span>
                  ) : (
                    <span className="status-tag MemeberListunregistered">
                      Unregistered
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="leaderboard-member-details">
              <div className="leaderboard-popup-row">
                <FaEnvelope className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Email:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.email}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaBuilding className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Department:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.department}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaPhoneAlt className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Mobile:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.mobile_phone || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaGlobe className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Nationality:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.nationality || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaCity className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">City:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.city || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <FaUsers className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Team:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.team || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <MdOutlineDeviceUnknown className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Device type:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile.device_type || "-"}
                  </div>
                </div>
              </div>
              <div className="leaderboard-popup-row">
                <MdOutlineNotificationsActive className="leaderboard-popup-icon" />
                <div className="leaderboard-popup-text">
                  <div className="leaderboard-popup-label">Last Active:</div>
                  <div className="leaderboard-popup-value">
                    {selectedUserProfile?.last_active
                      ? new Date(
                        selectedUserProfile.last_active
                      ).toLocaleString()
                      : "Never logged in"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamsMemberList;
